import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageContainer, PageHeader } from "@/components/app/PageShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
    Users, UsersThree, ShieldCheck, FilesIcon, Database, ArrowRight,
} from "@phosphor-icons/react";

const StatCard = ({ label, value, icon: Icon, to }) => (
    <Link to={to} className="brut-card p-6 group relative" data-testid={`stat-${label.toLowerCase()}`}>
        <div className="flex items-start justify-between">
            <div>
                <div className="label-overline mb-3">{label}</div>
                <div className="text-4xl font-bold tracking-tight">{value}</div>
            </div>
            <Icon size={20} weight="duotone" className="text-muted-foreground" />
        </div>
        <div className="mt-6 flex items-center text-xs text-muted-foreground group-hover:text-foreground transition-colors">
            <span>VIEW</span>
            <ArrowRight size={12} className="ml-1" />
        </div>
    </Link>
);

export default function DashboardHome() {
    const { user } = useAuth();
    const [stats, setStats] = useState({ users: 0, groups: 0, roles: 0, forms: 0, records: 0 });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/organizations/stats").then(({ data }) => setStats(data)).finally(() => setLoading(false));
    }, []);

    return (
        <PageContainer>
            <PageHeader
                overline={`Workspace · ${user?.org_name || ""}`}
                title={`Welcome, ${user?.name?.split(" ")[0] || "there"}.`}
                description="Foundation layer for your low-code platform. Build forms, manage access, and ship internal apps."
                actions={
                    <Link to="/forms"><Button className="rounded-sm bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="cta-new-form"><FilesIcon size={16} className="mr-2" /> Go to Forms</Button></Link>
                }
                testid="dashboard-header"
            />

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-border border border-border rounded-sm overflow-hidden mb-10">
                <div className="bg-background"><StatCardInline label="Forms" value={stats.forms} icon={FilesIcon} to="/forms" /></div>
                <div className="bg-background"><StatCardInline label="Records" value={stats.records} icon={Database} to="/forms" /></div>
                <div className="bg-background"><StatCardInline label="Users" value={stats.users} icon={Users} to="/users" /></div>
                <div className="bg-background"><StatCardInline label="Groups" value={stats.groups} icon={UsersThree} to="/groups" /></div>
                <div className="bg-background"><StatCardInline label="Roles" value={stats.roles} icon={ShieldCheck} to="/roles" /></div>
            </div>

            <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 brut-section p-8" data-testid="quickstart-card">
                    <div className="label-overline mb-2">Quick start</div>
                    <h2 className="text-2xl font-bold tracking-tight mb-6">Three steps to your first app</h2>
                    <ol className="space-y-5">
                        {[
                            { n: "01", t: "Define your data model", d: "Create a form, add sections and fields. We auto-generate per-form roles." },
                            { n: "02", t: "Configure access", d: "Build groups, assign roles, set default field values per group." },
                            { n: "03", t: "Capture records", d: "Use the dynamic renderer to add records. Search, sort, filter built-in." },
                        ].map((s) => (
                            <li key={s.n} className="flex gap-5 border-t border-border pt-5 first:border-t-0 first:pt-0">
                                <div className="font-mono text-sm text-muted-foreground w-8">{s.n}</div>
                                <div>
                                    <div className="font-semibold">{s.t}</div>
                                    <div className="text-sm text-muted-foreground mt-1">{s.d}</div>
                                </div>
                            </li>
                        ))}
                    </ol>
                </div>
                <div className="brut-section p-8">
                    <div className="label-overline mb-2">Architecture</div>
                    <h3 className="text-xl font-bold tracking-tight mb-4">Built for scale</h3>
                    <ul className="space-y-3 text-sm">
                        {["Shared MongoDB collections", "Per-org isolation", "Metadata-driven engine", "RBAC + groups", "Audit logging", "Server-side pagination"].map((x) => (
                            <li key={x} className="flex items-center gap-2 font-mono text-xs">
                                <span className="text-primary">▸</span> {x}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </PageContainer>
    );
}

const StatCardInline = ({ label, value, icon: Icon, to }) => (
    <Link to={to} className="block p-6 hover:bg-secondary transition-colors" data-testid={`stat-${label.toLowerCase()}`}>
        <div className="flex items-start justify-between mb-3">
            <div className="label-overline">{label}</div>
            <Icon size={16} weight="duotone" className="text-muted-foreground" />
        </div>
        <div className="text-3xl font-bold tracking-tight">{value}</div>
    </Link>
);
